import React, { useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Shield, Users, AlertTriangle, Activity, TrendingUp, Info, FileWarning, TrendingDown, Clock, Camera, Mail, ClipboardList, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useFilterParams, useFiltersStore } from "@/store/filtersStore";
import { fetchKPIs, fetchTrend, fetchLiderazgoPorActividad, fetchOPSPorTipo, fetchOPSTopPersonas, fetchSiniestKpis, fetchSiniestMensual, fetchKpisPorDelegacion, fetchCentrosActividad, fetchCumplimientoResponsables, fetchComparativaAnual, sendCumplimientoReminder, fetchPlanIncKpis, triggerSync } from '@/services/api';
import { KpiGlowCard } from '@/components/dashboard/KpiGlowCard';
import { ChartCard } from '@/components/dashboard/ChartCard';
import { TrendLineChart, HorizontalBarChart, DonutChart, VerticalBarChart } from '@/components/dashboard/Charts';
import { GlobalFilterBar } from '@/components/dashboard/GlobalFilterBar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CumplimientoPanel } from '@/components/common/CumplimientoPanel';
import { MisCentrosPanel } from '@/components/common/MisCentrosPanel';
import { SemaforoPanel } from '@/components/dashboard/SemaforoPanel';
import { DiasSinIncidentesPanel } from '@/components/dashboard/DiasSinIncidentesPanel';
import { TendenciasSection } from '@/components/dashboard/TendenciasSection';
import { useUserStore } from '@/store/userStore';
import { format, subMonths } from 'date-fns';

// "Last 12 months" rolling window — computed once at component render, not at module load
// Reminder email button for a single responsable row in Cumplimiento panel.
// Renders disabled (envelope icon greyed) when the responsable has no email
// override yet — admin must add it in Admin → Objetivos first.
function ReminderEmailButton({ row, desde, hasta }) {
  const [sending, setSending] = React.useState(false);
  const hasEmail = !!row?.email;
  const handleClick = async () => {
    if (!hasEmail || sending) return;
    setSending(true);
    try {
      const res = await sendCumplimientoReminder(row.responsable, { desde, hasta });
      toast.success(`Recordatorio enviado a ${res.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error enviando recordatorio');
    } finally {
      setSending(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!hasEmail || sending}
      title={hasEmail ? `Enviar recordatorio a ${row.email}` : 'Sin email configurado (Admin → Objetivos)'}
      data-testid={`cumpl-email-btn-${row.responsable}`}
      className={`inline-flex items-center justify-center w-7 h-7 rounded transition ${hasEmail ? 'hover:bg-black/10 cursor-pointer' : 'opacity-30 cursor-not-allowed'}`}
      style={{ color: 'var(--vz-text-muted)' }}
    >
      <Mail size={13} className={sending ? 'animate-pulse' : ''} />
    </button>
  );
}

function getDefaultRange() {
  return {
    desde: format(subMonths(new Date(), 12), 'yyyy-MM-dd'),
    hasta: format(new Date(), 'yyyy-MM-dd'),
  };
}

// Refresh every hour so charts stay current with daily syncs
const CHART_REFETCH_MS = 60 * 60 * 1000;

export default function Dashboard() {
  const filters = useFilterParams();
  const rawDesdeFilt = useFiltersStore(s => s.desde);
  const { token, user } = useUserStore();
  const isSuperAdmin = user?.role === 'superadmin';

  const syncMut = useMutation({
    mutationFn: triggerSync,
    onSuccess: () => toast.success('Sincronización iniciada', { description: 'Los datos de ArcGIS se están actualizando' }),
    onError: () => toast.error('No se pudo iniciar la sincronización'),
  });

  // Apply "last 12 months" default only when no explicit date filter is active
  const effectiveFilters = useMemo(() => {
    if (rawDesdeFilt) return filters;           // user already set a date filter
    const { desde, hasta } = getDefaultRange();
    return { ...filters, desde, hasta };
  }, [filters, rawDesdeFilt]);

  const queryOpts = { refetchInterval: CHART_REFETCH_MS, enabled: !!token };

  const { data: kpis, isLoading: kpiLoading } = useQuery({
    queryKey: ['kpis', effectiveFilters],
    queryFn: () => fetchKPIs(effectiveFilters),
    ...queryOpts,
  });

  const { data: trendOPS, isLoading: trendOpsLoading } = useQuery({
    queryKey: ['trend-ops', effectiveFilters],
    queryFn: () => fetchTrend({ ...effectiveFilters, tabla: 'ops', agrupacion: 'mes' }),
    ...queryOpts,
  });

  const { data: trendLid, isLoading: trendLidLoading } = useQuery({
    queryKey: ['trend-lid', effectiveFilters],
    queryFn: () => fetchTrend({ ...effectiveFilters, tabla: 'liderazgo', agrupacion: 'mes' }),
    ...queryOpts,
  });

  const { data: actividades, isLoading: actLoading } = useQuery({
    queryKey: ['lid-actividades', effectiveFilters],
    queryFn: () => fetchLiderazgoPorActividad(effectiveFilters),
    ...queryOpts,
  });

  const { data: tiposOPS, isLoading: tiposLoading } = useQuery({
    queryKey: ['ops-tipos', effectiveFilters],
    queryFn: () => fetchOPSPorTipo(effectiveFilters),
    ...queryOpts,
  });

  const { data: topPersonas, isLoading: personasLoading } = useQuery({
    queryKey: ['ops-personas', effectiveFilters],
    queryFn: () => fetchOPSTopPersonas({ ...effectiveFilters, limit: 8 }),
    ...queryOpts,
  });

  const { data: siniestKpis } = useQuery({
    queryKey: ['siniest-kpis-dashboard'],
    queryFn: () => fetchSiniestKpis({}),
    staleTime: 5 * 60 * 1000,
    enabled: !!token,
  });

  const { data: siniestMensual = [] } = useQuery({
    queryKey: ['siniest-mensual-dashboard'],
    queryFn: () => fetchSiniestMensual({}),
    staleTime: 5 * 60 * 1000,
    enabled: !!token,
  });

  const { data: porDelegacion = [], isLoading: delegLoading } = useQuery({
    queryKey: ['kpis-por-delegacion', filters],
    queryFn: () => fetchKpisPorDelegacion(filters),
    staleTime: 5 * 60 * 1000,
    enabled: !!token,
  });

  // D3 + D4 — Centros activos vs catálogo + silent list
  const { data: centrosAct } = useQuery({
    queryKey: ['centros-actividad', effectiveFilters.desde, effectiveFilters.hasta],
    queryFn: () => fetchCentrosActividad({ desde: effectiveFilters.desde, hasta: effectiveFilters.hasta }),
    staleTime: 5 * 60 * 1000,
    enabled: !!token,
  });
  const [silentModalOpen, setSilentModalOpen] = React.useState(false);

  // D1 + D5 + D7 — Cumplimiento mensual por responsable (Luis 2026)
  const { data: cumpl } = useQuery({
    queryKey: ['cumplimiento-responsables', effectiveFilters.desde, effectiveFilters.hasta],
    queryFn: () => fetchCumplimientoResponsables({ desde: effectiveFilters.desde, hasta: effectiveFilters.hasta }),
    staleTime: 5 * 60 * 1000,
    enabled: !!token,
  });

  // D2 — Comparativa periodo actual vs mismo periodo año anterior
  const { data: comparativa } = useQuery({
    queryKey: ['comparativa-anual', effectiveFilters.desde, effectiveFilters.hasta],
    queryFn: () => fetchComparativaAnual({ desde: effectiveFilters.desde, hasta: effectiveFilters.hasta }),
    staleTime: 5 * 60 * 1000,
    enabled: !!token,
  });

  // Plan Inicio Jornada KPI (new 2026 OPS checklist)
  const { data: planIncData } = useQuery({
    queryKey: ['planinc-kpis', effectiveFilters.desde, effectiveFilters.hasta, effectiveFilters.delegacion, effectiveFilters.gerente],
    queryFn: () => fetchPlanIncKpis({
      desde: effectiveFilters.desde,
      hasta: effectiveFilters.hasta,
      delegacion: effectiveFilters.delegacion,
      gerente: effectiveFilters.gerente,
    }),
    staleTime: 5 * 60 * 1000,
    enabled: !!token,
  });

  return (
    <div data-testid="dashboard-page">
      <GlobalFilterBar />

      <div className="px-4 lg:px-6 py-6 space-y-6">
        {/* ── Botón Forzar Sync (solo superadmin) ─────────────────────── */}
        {isSuperAdmin && (
          <div
            className="rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            style={{
              background: 'linear-gradient(135deg, rgba(74,158,255,0.1) 0%, rgba(0,200,83,0.05) 100%)',
              border: '1.5px solid rgba(74,158,255,0.3)',
              boxShadow: '0 0 24px rgba(74,158,255,0.06)',
            }}
            data-testid="dashboard-sync-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(74,158,255,0.15)', border: '1px solid rgba(74,158,255,0.3)' }}>
                <RefreshCw size={20} style={{ color: '#4A9EFF' }} />
              </div>
              <div>
                <p className="text-sm font-bold font-montserrat" style={{ color: 'var(--vz-text-primary)' }}>
                  Sincronización con ArcGIS
                </p>
                <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>
                  Automatica cada 3 h · Fuerza una actualización inmediata si faltan datos recientes
                </p>
              </div>
            </div>
            <button
              onClick={() => syncMut.mutate()}
              disabled={syncMut.isPending}
              data-testid="dashboard-force-sync-btn"
              className="flex items-center gap-2 font-semibold text-sm px-6 h-11 rounded-xl flex-shrink-0 transition-all"
              style={{
                background: syncMut.isPending ? 'rgba(74,158,255,0.08)' : 'rgba(74,158,255,0.18)',
                color: '#4A9EFF',
                border: '1.5px solid rgba(74,158,255,0.4)',
                boxShadow: syncMut.isPending ? 'none' : '0 2px 14px rgba(74,158,255,0.18)',
                cursor: syncMut.isPending ? 'not-allowed' : 'pointer',
              }}
            >
              <RefreshCw size={16} className={syncMut.isPending ? 'animate-spin' : ''} />
              {syncMut.isPending ? 'Sincronizando...' : 'Forzar Sincronización'}
            </button>
          </div>
        )}

        {/* KPI Row */}
        <div data-testid="dashboard-kpi-row" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-5">
          <KpiGlowCard
            title="Total OPS"
            value={kpis?.totalOPS ?? 0}
            color="#4A9EFF"
            icon={Shield}
            loading={kpiLoading}
            tooltip="Número total de Observaciones Preventivas de Seguridad registradas en el período seleccionado. Incluye todos los tipos de observación: EPIs, entorno, vehículos, procedimientos, etc."
          />
          <KpiGlowCard
            title="Total Liderazgo"
            value={kpis?.totalLiderazgo ?? 0}
            color="#00C853"
            icon={Users}
            loading={kpiLoading}
            tooltip="Total de actividades de liderazgo realizadas en el período: charlas motivacionales, ASTs, inspecciones, formaciones y otras actividades de liderazgo activo."
          />
          <KpiGlowCard
            title="Near Misses (ArcGIS)"
            value={kpis?.totalNearMisses ?? 0}
            color="#FF6B35"
            icon={AlertTriangle}
            loading={kpiLoading}
            tooltip="Cuasi-accidentes reportados vía la app de campo (ArcGIS Survey123). Esta cifra NO incluye los Near Miss registrados en el Excel de Siniestralidad (PRL) — son dos fuentes distintas, ver sección Siniestralidad más abajo."
          />
          <KpiGlowCard
            title="Asistentes"
            value={kpis?.totalAsistentes ?? 0}
            color="#AB47BC"
            icon={Activity}
            loading={kpiLoading}
            tooltip="Suma total de participantes en todas las actividades de liderazgo del período. Mide el alcance e impacto de las iniciativas formativas y de concienciación."
          />
        </div>

        {/* Incumplimiento banner */}
        {kpis && (
          <div
            className="rounded-xl border px-4 py-3 flex items-center justify-between"
            style={{
              background: kpis.incumplimientoRate > 15 ? 'rgba(255,82,82,0.08)' : 'rgba(0,200,83,0.06)',
              borderColor: kpis.incumplimientoRate > 15 ? 'rgba(255,82,82,0.3)' : 'rgba(0,200,83,0.2)',
            }}
          >
            <div className="flex items-center gap-3">
              <Shield size={16} style={{ color: kpis.incumplimientoRate > 15 ? '#FF5252' : '#00C853' }} />
              <span className="font-montserrat text-xs font-semibold" style={{ color: 'var(--vz-text-secondary)' }}>
                Tasa de incumplimiento RSV
              </span>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={11} style={{ color: 'var(--vz-text-muted)', cursor: 'help' }} />
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-[240px] text-xs leading-snug"
                    style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-secondary)' }}
                  >
                    Porcentaje de OPS con incumplimiento RSV (Riesgo de Seguridad Vial) sobre el total de OPS. Se calcula como: (OPS con incumplimiento = SÍ ÷ Total OPS) × 100. Alerta en rojo si supera el 15%.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <span
              className="font-figtree text-xl font-bold tabular-nums"
              style={{ color: kpis.incumplimientoRate > 15 ? '#FF5252' : '#00C853' }}
            >
              {kpis.incumplimientoRate.toFixed(1)}%
            </span>
          </div>
        )}

        {/* D2 — Comparativa YoY (mismo periodo año anterior) */}
        {comparativa && comparativa.actual && (
          <div
            data-testid="comparativa-anual-banner"
            className="rounded-xl border px-4 py-3"
            style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}
          >
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <span className="font-montserrat text-xs font-semibold" style={{ color: 'var(--vz-text-secondary)' }}>
                Comparativa YoY
              </span>
              <span className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>
                vs mismo periodo año anterior ({comparativa.rango_anterior?.desde} → {comparativa.rango_anterior?.hasta})
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { key: 'ops', label: 'OPS', invert: false },
                { key: 'liderazgo', label: 'Liderazgo', invert: false },
                { key: 'near_misses', label: 'NM ArcGIS', invert: false },
                { key: 'accidentes', label: 'Accidentes', invert: true },
              ].map(({ key, label, invert }) => {
                const cur = comparativa.actual[key] ?? 0;
                const prev = comparativa.anterior[key] ?? 0;
                const delta = comparativa.delta[key];
                // For accidentes, going down is good (invert color logic)
                const positive = delta == null ? null : (invert ? delta < 0 : delta > 0);
                const colour = positive == null ? 'var(--vz-text-muted)' : positive ? '#00A847' : '#E53935';
                return (
                  <div key={key} className="rounded p-2" style={{ background: 'var(--vz-bg-card)', border: '1px solid var(--vz-border-subtle)' }}>
                    <div className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--vz-text-muted)' }}>{label}</div>
                    <div className="flex items-baseline gap-2 mt-0.5">
                      <span className="font-figtree text-base font-bold tabular-nums" style={{ color: 'var(--vz-text-primary)' }}>{cur.toLocaleString('es-ES')}</span>
                      <span className="text-[10px] tabular-nums" style={{ color: 'var(--vz-text-muted)' }}>(vs {prev.toLocaleString('es-ES')})</span>
                    </div>
                    <div className="text-[10px] font-semibold tabular-nums" style={{ color: colour }} title={positive == null ? 'n.a. (base insuficiente): se requieren al menos 10 registros en el periodo anterior para comparar' : ''}>
                      {delta == null ? 'n.a.' : (delta > 0 ? '▲ +' : delta < 0 ? '▼ ' : '◆ ')}{delta == null ? '' : `${Math.abs(delta).toFixed(1)}%`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* D6 — Pirámide de Heinrich: Ratio Accidentes / Near Misses */}
        {kpis && (
          <div
            data-testid="safety-pyramid-banner"
            className="rounded-xl border px-4 py-3 flex items-center justify-between"
            style={{
              background: (kpis.ratioAccidentesNM == null || kpis.ratioAccidentesNM >= 1)
                ? 'rgba(255,82,82,0.08)'
                : kpis.ratioAccidentesNM > 0.3 ? 'rgba(255,193,7,0.08)' : 'rgba(0,200,83,0.06)',
              borderColor: (kpis.ratioAccidentesNM == null || kpis.ratioAccidentesNM >= 1)
                ? 'rgba(255,82,82,0.3)'
                : kpis.ratioAccidentesNM > 0.3 ? 'rgba(255,193,7,0.3)' : 'rgba(0,200,83,0.2)',
            }}
          >
            <div className="flex items-center gap-3">
              <AlertTriangle size={16} style={{
                color: (kpis.ratioAccidentesNM == null || kpis.ratioAccidentesNM >= 1)
                  ? '#FF5252'
                  : kpis.ratioAccidentesNM > 0.3 ? '#FFC107' : '#00C853'
              }} />
              <span className="font-montserrat text-xs font-semibold" style={{ color: 'var(--vz-text-secondary)' }}>
                Ratio Accidentes / NM ArcGIS
              </span>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={11} style={{ color: 'var(--vz-text-muted)', cursor: 'help' }} />
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-[280px] text-xs leading-snug"
                    style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-secondary)' }}
                  >
                    Aplica la pirámide de Heinrich: un ratio bajo (&lt;0.3) indica buena cultura de reporte. Un ratio alto (≥1) sugiere que se reportan más accidentes que cuasi-accidentes y posiblemente hay subreporte. El NM usado aquí es el reportado en ArcGIS, no el del Excel de Siniestralidad.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <span className="text-[11px] tabular-nums" style={{ color: 'var(--vz-text-muted)' }}>
                ({kpis.totalAccidentes ?? 0} accidentes · {kpis.totalNearMisses ?? 0} NM)
              </span>
            </div>
            <span
              className="font-figtree text-xl font-bold tabular-nums"
              style={{
                color: (kpis.ratioAccidentesNM == null || kpis.ratioAccidentesNM >= 1)
                  ? '#FF5252'
                  : kpis.ratioAccidentesNM > 0.3 ? '#FFC107' : '#00C853'
              }}
            >
              {kpis.ratioAccidentesNM == null ? 'N/A' : kpis.ratioAccidentesNM.toFixed(2)}
            </span>
          </div>
        )}

        {/* D3 — Centros activos vs catálogo */}
        {centrosAct && centrosAct.total_centros > 0 && (
          <div
            data-testid="centros-actividad-banner"
            className="rounded-xl border px-4 py-3 flex items-center justify-between"
            style={{
              background: centrosAct.pct_activos >= 80
                ? 'rgba(0,200,83,0.06)'
                : centrosAct.pct_activos >= 50 ? 'rgba(255,193,7,0.08)' : 'rgba(255,82,82,0.08)',
              borderColor: centrosAct.pct_activos >= 80
                ? 'rgba(0,200,83,0.2)'
                : centrosAct.pct_activos >= 50 ? 'rgba(255,193,7,0.3)' : 'rgba(255,82,82,0.3)',
            }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <Activity size={16} style={{
                color: centrosAct.pct_activos >= 80 ? '#00C853'
                  : centrosAct.pct_activos >= 50 ? '#FFC107' : '#FF5252'
              }} />
              <span className="font-montserrat text-xs font-semibold whitespace-nowrap" style={{ color: 'var(--vz-text-secondary)' }}>
                Centros activos
              </span>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={11} style={{ color: 'var(--vz-text-muted)', cursor: 'help' }} />
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-[280px] text-xs leading-snug"
                    style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-secondary)' }}
                  >
                    Centros del catálogo Valoriza con al menos 1 OPS, Liderazgo o Near Miss en el periodo. Identifica delegaciones sin reporte preventivo (potencial subreporte).
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <span className="text-[11px] tabular-nums truncate" style={{ color: 'var(--vz-text-muted)' }}>
                {centrosAct.centros_activos} de {centrosAct.total_centros} centros
                {centrosAct.centros_sin_actividad > 0 && (
                  <>
                    {' · '}
                    <button
                      type="button"
                      onClick={() => setSilentModalOpen(true)}
                      data-testid="silent-centros-btn"
                      className="underline hover:no-underline cursor-pointer"
                      style={{ color: '#FF5252' }}
                    >
                      {centrosAct.centros_sin_actividad} sin actividad
                    </button>
                  </>
                )}
              </span>
            </div>
            <span
              className="font-figtree text-xl font-bold tabular-nums"
              style={{
                color: centrosAct.pct_activos >= 80 ? '#00C853'
                  : centrosAct.pct_activos >= 50 ? '#FFC107' : '#FF5252'
              }}
            >
              {centrosAct.pct_activos.toFixed(1)}%
            </span>
          </div>
        )}

        {/* D4 — Silent centres modal */}
        {silentModalOpen && centrosAct && (
          <div
            data-testid="silent-centros-modal"
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.55)' }}
            onClick={() => setSilentModalOpen(false)}
          >
            <div
              className="rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col"
              style={{ background: 'var(--vz-bg-card)', border: '1px solid var(--vz-border-subtle)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--vz-border-subtle)' }}>
                <div>
                  <h3 className="font-montserrat font-bold text-sm" style={{ color: 'var(--vz-text-primary)' }}>
                    Centros sin actividad preventiva
                  </h3>
                  <p className="text-[11px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
                    {centrosAct.centros_sin_actividad} centros sin OPS, Liderazgo ni Near Misses en el periodo
                  </p>
                </div>
                <button
                  onClick={() => setSilentModalOpen(false)}
                  className="text-xs px-2 py-1 rounded hover:bg-black/10"
                  style={{ color: 'var(--vz-text-muted)' }}
                  data-testid="silent-modal-close"
                >
                  ✕ Cerrar
                </button>
              </div>
              <div className="overflow-y-auto p-4 space-y-1">
                {centrosAct.sin_actividad.length === 0 ? (
                  <p className="text-xs text-center py-8" style={{ color: 'var(--vz-text-muted)' }}>
                    Todos los centros han tenido actividad ✨
                  </p>
                ) : (
                  centrosAct.sin_actividad.map((c) => (
                    <div
                      key={c.codigo}
                      className="flex items-center justify-between gap-3 px-3 py-2 rounded hover:bg-black/5"
                      style={{ borderBottom: '1px solid var(--vz-border-subtle)' }}
                    >
                      <span className="text-xs flex-1 truncate" style={{ color: 'var(--vz-text-primary)' }} title={c.nombre}>
                        {c.nombre || '—'}
                      </span>
                      <span className="text-[10px] font-mono" style={{ color: 'var(--vz-text-muted)' }}>
                        {c.codigo}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* D1 + D5 + D7 — Cumplimiento mensual por responsable (Luis 2026) */}
        {/* Mis Centros — actividad reciente por sede (solo usuarios con RLS) */}
        <MisCentrosPanel />

        {cumpl && cumpl.responsables && cumpl.responsables.length > 0 && (
          <div
            data-testid="cumplimiento-responsables-panel"
            className="rounded-xl border p-4"
            style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}
          >
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <h3 className="font-montserrat font-bold text-sm" style={{ color: 'var(--vz-text-primary)' }}>
                  Cumplimiento objetivos por responsable
                </h3>
                <p className="text-[11px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
                  OPS y Liderazgo realizados vs objetivo mensual (Luis 2026) · {cumpl.rango.meses} {cumpl.rango.meses === 1 ? 'mes' : 'meses'} en el rango
                </p>
              </div>
              <div className="flex items-center gap-4 text-[11px]">
                <div className="flex items-center gap-1.5">
                  <span style={{ color: 'var(--vz-text-muted)' }}>OPS global</span>
                  <span className="font-bold" style={{
                    color: cumpl.totales.pct_ops >= 90 ? '#00A847'
                      : cumpl.totales.pct_ops >= 60 ? '#D4A300' : '#E53935'
                  }}>
                    {cumpl.totales.pct_ops}%
                  </span>
                  <span style={{ color: 'var(--vz-text-muted)' }}>({cumpl.totales.ops_real}/{cumpl.totales.objetivo_ops})</span>
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info size={11} style={{ color: 'var(--vz-text-muted)', cursor: 'help' }} />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[240px] text-xs leading-snug" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-secondary)' }}>
                        Distinto del "Total OPS" de arriba: aquí se compara el nº de OPS realizadas contra el objetivo mensual asignado a cada responsable (Excel Luis 2026), no el total absoluto de registros del período.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center gap-1.5">
                  <span style={{ color: 'var(--vz-text-muted)' }}>Liderazgo global</span>
                  <span className="font-bold" style={{
                    color: cumpl.totales.pct_lid >= 90 ? '#00A847'
                      : cumpl.totales.pct_lid >= 60 ? '#D4A300' : '#E53935'
                  }}>
                    {cumpl.totales.pct_lid}%
                  </span>
                  <span style={{ color: 'var(--vz-text-muted)' }}>({cumpl.totales.lid_real}/{cumpl.totales.objetivo_lid})</span>
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info size={11} style={{ color: 'var(--vz-text-muted)', cursor: 'help' }} />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[240px] text-xs leading-snug" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-secondary)' }}>
                        Distinto del "Total Liderazgo" de arriba: compara actividades realizadas contra el objetivo mensual asignado (Excel Luis 2026).
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center gap-1.5">
                  <span style={{ color: 'var(--vz-text-muted)' }}>Personas OPS</span>
                  <span className="font-bold" style={{ color: 'var(--vz-text-primary)' }}>
                    {cumpl.totales.personas_ops_real}/{cumpl.totales.personas_ops_target}
                  </span>
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info size={11} style={{ color: 'var(--vz-text-muted)', cursor: 'help' }} />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[240px] text-xs leading-snug" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-secondary)' }}>
                        Personas que han realizado al menos 1 OPS en el período, sobre el nº de personas objetivo (headcount del Excel Luis 2026). No es el mismo dato que "Total OPS".
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--vz-border-subtle)' }}>
                    <th className="text-left py-2 pr-2 font-semibold" style={{ color: 'var(--vz-text-muted)' }}>Responsable</th>
                    <th className="text-right py-2 px-2 font-semibold" style={{ color: 'var(--vz-text-muted)' }}>OPS</th>
                    <th className="text-right py-2 px-2 font-semibold" style={{ color: 'var(--vz-text-muted)' }}>% OPS</th>
                    <th className="text-right py-2 px-2 font-semibold" style={{ color: 'var(--vz-text-muted)' }}>Liderazgo</th>
                    <th className="text-right py-2 px-2 font-semibold" style={{ color: 'var(--vz-text-muted)' }}>% Lid</th>
                    <th className="text-right py-2 px-2 font-semibold" style={{ color: 'var(--vz-text-muted)' }} title="Personas que han hecho ≥1 OPS / personas objetivo (Excel Luis)">
                      Personas
                    </th>
                    <th className="text-center py-2 pl-2 font-semibold" style={{ color: 'var(--vz-text-muted)' }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {cumpl.responsables.map((r) => (
                    <tr key={r.responsable} data-testid={`cumpl-row-${r.responsable}`} style={{ borderBottom: '1px solid var(--vz-border-subtle)' }}>
                      <td className="py-1.5 pr-2" style={{ color: 'var(--vz-text-primary)' }}>
                        <div className="font-medium uppercase">{r.responsable}</div>
                        <div className="text-[10px] font-mono opacity-60" style={{ color: 'var(--vz-text-muted)' }}>{r.zonas.join(', ')}</div>
                      </td>
                      <td className="text-right py-1.5 px-2 tabular-nums" style={{ color: 'var(--vz-text-secondary)' }}>
                        {r.ops_real}/{r.objetivo_ops}
                      </td>
                      <td className="text-right py-1.5 px-2 tabular-nums font-bold" style={{
                        color: r.pct_ops == null ? 'var(--vz-text-muted)'
                          : r.pct_ops >= 90 ? '#00A847'
                          : r.pct_ops >= 60 ? '#D4A300' : '#E53935'
                      }}>
                        {r.pct_ops == null ? '—' : `${r.pct_ops}%`}
                      </td>
                      <td className="text-right py-1.5 px-2 tabular-nums" style={{ color: 'var(--vz-text-secondary)' }}>
                        {r.lid_real}/{r.objetivo_lid}
                      </td>
                      <td className="text-right py-1.5 px-2 tabular-nums font-bold" style={{
                        color: r.pct_lid == null ? 'var(--vz-text-muted)'
                          : r.pct_lid >= 90 ? '#00A847'
                          : r.pct_lid >= 60 ? '#D4A300' : '#E53935'
                      }}>
                        {r.pct_lid == null ? '—' : `${r.pct_lid}%`}
                      </td>
                      <td className="text-right py-1.5 px-2 tabular-nums" style={{
                        color: r.personas_real < r.personas_target ? '#E53935' : 'var(--vz-text-secondary)'
                      }}>
                        {r.personas_real}/{r.personas_target}
                      </td>
                      <td className="text-center py-1.5 pl-2">
                        <ReminderEmailButton row={r} desde={effectiveFilters.desde} hasta={effectiveFilters.hasta} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Foto KPI banner */}
        {kpis && (kpis.opsFotoPct > 0 || kpis.lidFotoPct > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { label: 'OPS con evidencia fotográfica', pct: kpis.opsFotoPct ?? 0, color: '#4A9EFF', tooltip: 'Porcentaje de Observaciones Preventivas de Seguridad que incluyen al menos una fotografía adjunta como evidencia.' },
              { label: 'Liderazgo con evidencia fotográfica', pct: kpis.lidFotoPct ?? 0, color: '#00C853', tooltip: 'Porcentaje de actividades de liderazgo que incluyen al menos una fotografía adjunta como evidencia.' },
            ].map(({ label, pct, color, tooltip }) => (
              <TooltipProvider key={label} delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="rounded-xl border px-4 py-3 flex items-center justify-between cursor-help"
                      data-testid={`dashboard-foto-kpi-${label.includes('OPS') ? 'ops' : 'lid'}`}
                      style={{
                        background: `${color}09`,
                        borderColor: `${color}28`,
                        boxShadow: `0 0 0 1px ${color}14`,
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <Camera size={16} style={{ color }} />
                        <span className="font-montserrat text-xs font-semibold" style={{ color: 'var(--vz-text-secondary)' }}>{label}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-24 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                        </div>
                        <span className="font-figtree text-lg font-bold tabular-nums" style={{ color }}>{pct.toFixed(1)}%</span>
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[240px] text-xs leading-snug"
                    style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-secondary)' }}>
                    {tooltip}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        )}

        {/* Plan Inicio Jornada KPI Section */}
        {planIncData && planIncData.total > 0 && (
          <div data-testid="dashboard-planinc-section">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList size={14} style={{ color: 'var(--vz-green)' }} />
              <span className="font-montserrat text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--vz-text-muted)' }}>
                Plan de Inicio de Jornada — Cumplimiento del Checklist
              </span>
            </div>
            <div className="rounded-xl border p-4" style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border)' }}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                {/* Main pct circle */}
                <div className="flex flex-col items-center min-w-[80px]">
                  <span className="text-3xl font-bold tabular-nums" style={{ color: planIncData.pct_cumplimiento >= 90 ? 'var(--vz-green)' : planIncData.pct_cumplimiento >= 70 ? 'var(--vz-orange)' : 'var(--vz-red)' }}>
                    {planIncData.pct_cumplimiento}%
                  </span>
                  <span className="text-[10px] uppercase tracking-wide mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>Cumplimiento</span>
                  <span className="text-[10px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>{planIncData.total} registros</span>
                </div>
                {/* Section breakdown */}
                <div className="flex-1 grid grid-cols-3 gap-3">
                  {Object.values(planIncData.por_seccion || {}).map(sec => (
                    <div key={sec.label} className="text-center">
                      <div className="text-sm font-bold tabular-nums" style={{ color: sec.pct >= 90 ? 'var(--vz-green)' : sec.pct >= 70 ? 'var(--vz-orange)' : 'var(--vz-red)' }}>
                        {sec.pct}%
                      </div>
                      <div className="text-[10px] leading-tight mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>{sec.label}</div>
                    </div>
                  ))}
                </div>
                {/* Alerts */}
                <div className="flex flex-col items-center min-w-[70px]">
                  <span className="text-xl font-bold" style={{ color: planIncData.con_alertas > 0 ? 'var(--vz-orange)' : 'var(--vz-green)' }}>
                    {planIncData.con_alertas}
                  </span>
                  <span className="text-[10px] uppercase tracking-wide text-center" style={{ color: 'var(--vz-text-muted)' }}>Con NO</span>
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--vz-bg-elevated)' }}>
                <div className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${planIncData.pct_cumplimiento}%`, background: planIncData.pct_cumplimiento >= 90 ? 'var(--vz-green)' : planIncData.pct_cumplimiento >= 70 ? 'var(--vz-orange)' : 'var(--vz-red)' }} />
              </div>
            </div>
          </div>
        )}

        {/* Siniestralidad KPI Section */}
        {siniestKpis && (siniestKpis.total_acc_baja > 0 || siniestKpis.total_centros > 0) && (
          <div data-testid="dashboard-siniest-section">
            <div className="flex items-center gap-2 mb-3">
              <FileWarning size={14} style={{ color: 'var(--vz-red)' }} />
              <span className="font-montserrat text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--vz-text-muted)' }}>
                Siniestralidad — Indicadores anuales
              </span>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={11} style={{ color: 'var(--vz-text-muted)', cursor: 'help' }} />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[240px] text-xs leading-snug"
                    style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-secondary)' }}>
                    Métricas del módulo de siniestralidad laboral basadas en el último import Excel. No se filtran por el período del dashboard.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
              {[
                {
                  label: 'Acc. con Baja', value: siniestKpis.total_acc_baja,
                  color: 'var(--vz-red)', icon: AlertTriangle,
                  tooltip: 'Total de accidentes laborales con baja registrados en el período del Excel importado.',
                },
                {
                  label: 'Jornadas Perdidas', value: siniestKpis.total_jp,
                  color: 'var(--vz-orange)', icon: Clock,
                  tooltip: 'Suma total de jornadas de trabajo perdidas por accidentes con baja.',
                },
                {
                  label: 'Índice Frecuencia', value: siniestKpis.if_global,
                  color: 'var(--vz-blue)', icon: Activity,
                  tooltip: 'IF = (Accidentes con baja × 1.000.000) ÷ Horas trabajadas. Número de accidentes por millón de horas.',
                },
                {
                  label: 'Índice Gravedad', value: siniestKpis.ig_global,
                  color: '#AB47BC', icon: TrendingDown,
                  tooltip: 'IG = (Jornadas perdidas × 1.000) ÷ Horas trabajadas. Número de jornadas perdidas por cada mil horas trabajadas.',
                },
                {
                  label: 'Near Misses (Excel PRL)', value: siniestKpis.total_nm,
                  color: '#00BCD4', icon: AlertTriangle,
                  tooltip: 'Cuasi-accidentes registrados en el Excel de Siniestralidad (PRL) importado por Admin. Cifra distinta e independiente de "Near Misses (ArcGIS)" del bloque superior — no se deben sumar ni comparar directamente.',
                },
                {
                  label: 'Centros activos', value: siniestKpis.total_centros,
                  color: 'var(--vz-green)', icon: Shield,
                  tooltip: 'Número de centros de trabajo con datos de siniestralidad importados.',
                },
              ].map(({ label, value, color, icon: Icon, tooltip }) => (
                <TooltipProvider key={label} delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className="rounded-xl p-3.5 flex flex-col gap-1.5 cursor-help"
                        style={{
                          background: 'var(--vz-bg-card)',
                          border: `1px solid ${color}28`,
                          boxShadow: `0 0 0 1px ${color}14, 0 2px 12px ${color}0a`,
                        }}
                        data-testid={`dashboard-siniest-kpi-${label.toLowerCase().replace(/\s/g,'-')}`}
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-[9px] font-montserrat font-semibold uppercase tracking-wider" style={{ color: 'var(--vz-text-muted)' }}>{label}</p>
                          <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: `${color}18` }}>
                            <Icon size={10} style={{ color }} />
                          </div>
                        </div>
                        <p className="font-montserrat font-bold text-xl tabular-nums" style={{ color }}>{value ?? '—'}</p>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[200px] text-xs leading-snug"
                      style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-secondary)' }}>
                      {tooltip}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>

            {/* Mini trend line for accident evolution */}
            {siniestMensual.length > 0 && (
              <div className="mt-3">
                <ChartCard
                  title="Evolución accidentes con baja — mensual"
                  subtitle="Datos del último Excel importado · Sin filtro de fecha"
                >
                  <VerticalBarChart
                    data={siniestMensual.map(m => ({ name: m.mes.slice(0, 3), value: m.acc_baja }))}
                    dataKey="value"
                    color="var(--vz-red)"
                  />
                </ChartCard>
              </div>
            )}
          </div>
        )}

        {/* Trend Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
          <ChartCard
            title="Evolución OPS por mes"
            subtitle="Observaciones de seguridad registradas"
            loading={trendOpsLoading}
            isEmpty={!trendOPS?.length}
          >
            <TrendLineChart data={trendOPS || []} color="#4A9EFF" label="OPS" />
          </ChartCard>

          <ChartCard
            title="Evolución Liderazgo por mes"
            subtitle="Actividades de liderazgo realizadas"
            loading={trendLidLoading}
            isEmpty={!trendLid?.length}
          >
            <TrendLineChart data={trendLid || []} color="#00C853" label="Liderazgo" />
          </ChartCard>
        </div>

        {/* Comparativa por Delegación — Survey123 spec 2026-04-28 */}
        <ChartCard
          title="Comparativa por Delegación"
          subtitle="Total OPS, Liderazgo, Near Miss y % cumplimiento RSV por delegación"
          loading={delegLoading}
          isEmpty={!porDelegacion?.length}
          emptyMessage="Aún no hay datos de delegación. Llegarán automáticamente conforme los operarios envíen formularios actualizados."
        >
          <div className="overflow-x-auto" data-testid="dashboard-por-delegacion">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--vz-border-subtle)' }}>
                  <th className="text-left py-2 px-2 font-semibold uppercase tracking-wide text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>Delegación</th>
                  <th className="text-right py-2 px-2 font-semibold uppercase tracking-wide text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>OPS</th>
                  <th className="text-right py-2 px-2 font-semibold uppercase tracking-wide text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>% Incum. OPS</th>
                  <th className="text-right py-2 px-2 font-semibold uppercase tracking-wide text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>Liderazgo</th>
                  <th className="text-right py-2 px-2 font-semibold uppercase tracking-wide text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>Asistentes</th>
                  <th className="text-right py-2 px-2 font-semibold uppercase tracking-wide text-[10px]" style={{ color: 'var(--vz-text-muted)' }} title="No disponible: ArcGIS/Survey123 no envía este campo en Liderazgo todavía">% Incum. LID</th>
                  <th className="text-right py-2 px-2 font-semibold uppercase tracking-wide text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>NM ArcGIS</th>
                </tr>
              </thead>
              <tbody>
                {porDelegacion.map((d) => (
                  <tr key={d.delegacion} style={{ borderBottom: '1px solid var(--vz-border-subtle)' }} data-testid={`delegacion-row-${d.delegacion}`}>
                    <td className="py-2.5 px-2 font-medium" style={{ color: 'var(--vz-text-primary)' }}>{d.delegacion}</td>
                    <td className="py-2.5 px-2 text-right font-mono" style={{ color: '#4A9EFF', fontWeight: 600 }}>{d.ops_total.toLocaleString('es-ES')}</td>
                    <td className="py-2.5 px-2 text-right font-mono" style={{ color: d.ops_incum_pct > 15 ? '#F44336' : d.ops_incum_pct > 5 ? '#F97316' : '#4CAF50' }}>{d.ops_incum_pct}%</td>
                    <td className="py-2.5 px-2 text-right font-mono" style={{ color: '#00C853', fontWeight: 600 }}>{d.lid_total.toLocaleString('es-ES')}</td>
                    <td className="py-2.5 px-2 text-right font-mono" style={{ color: 'var(--vz-text-secondary)' }}>{d.lid_asistentes.toLocaleString('es-ES')}</td>
                    <td className="py-2.5 px-2 text-right font-mono" style={{ color: 'var(--vz-text-muted)' }} title="No disponible: ArcGIS/Survey123 no envía este campo en Liderazgo todavía">—</td>
                    <td className="py-2.5 px-2 text-right font-mono" style={{ color: d.nm_total > 0 ? '#FF9800' : 'var(--vz-text-muted)', fontWeight: 600 }}>{d.nm_total.toLocaleString('es-ES')}</td>
                  </tr>
                ))}
              </tbody>
              {porDelegacion.length > 1 && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--vz-border-subtle)' }}>
                    <td className="py-2.5 px-2 font-bold uppercase text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>Total</td>
                    <td className="py-2.5 px-2 text-right font-mono font-bold" style={{ color: '#4A9EFF' }}>
                      {porDelegacion.reduce((s, d) => s + d.ops_total, 0).toLocaleString('es-ES')}
                    </td>
                    <td className="py-2.5 px-2 text-right font-mono" style={{ color: 'var(--vz-text-muted)' }}>—</td>
                    <td className="py-2.5 px-2 text-right font-mono font-bold" style={{ color: '#00C853' }}>
                      {porDelegacion.reduce((s, d) => s + d.lid_total, 0).toLocaleString('es-ES')}
                    </td>
                    <td className="py-2.5 px-2 text-right font-mono font-bold" style={{ color: 'var(--vz-text-secondary)' }}>
                      {porDelegacion.reduce((s, d) => s + d.lid_asistentes, 0).toLocaleString('es-ES')}
                    </td>
                    <td className="py-2.5 px-2 text-right font-mono" style={{ color: 'var(--vz-text-muted)' }}>—</td>
                    <td className="py-2.5 px-2 text-right font-mono font-bold" style={{ color: '#FF9800' }}>
                      {porDelegacion.reduce((s, d) => s + d.nm_total, 0).toLocaleString('es-ES')}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </ChartCard>

        {/* Breakdowns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5">
          <ChartCard
            title="Top 8 personas OPS"
            subtitle="Mayor número de observaciones"
            loading={personasLoading}
            isEmpty={!topPersonas?.length}
          >
            <HorizontalBarChart
              data={(topPersonas || []).map((p) => ({ name: p.persona, total: p.total }))}
              nameKey="name"
              dataKey="total"
              color="#4A9EFF"
              height={200}
            />
          </ChartCard>

          <ChartCard
            title="Tipos de riesgo OPS"
            subtitle="Distribución por tipo de observación"
            loading={tiposLoading}
            isEmpty={!tiposOPS?.length}
          >
            <DonutChart
              data={(tiposOPS || []).slice(0, 6).map((t) => ({ name: t.tipo, total: t.total }))}
              nameKey="name"
              dataKey="total"
              height={200}
            />
          </ChartCard>

          <ChartCard
            title="Actividades de liderazgo"
            subtitle="Por tipo de actividad realizada"
            loading={actLoading}
            isEmpty={!actividades?.length}
          >
            <HorizontalBarChart
              data={(actividades || []).slice(0, 6).map((a) => ({ name: a.actividad, total: a.total }))}
              nameKey="name"
              dataKey="total"
              color="#00C853"
              height={200}
            />
          </ChartCard>
        </div>

        {/* Cumplimiento por persona */}
        <ChartCard
          title="Cumplimiento por persona"
          subtitle="Contador de OPS y Liderazgo realizados por cada persona"
        >
          <CumplimientoPanel />
        </ChartCard>

        {/* ── Semáforo de riesgo por centro ──────────────────────────────── */}
        <div
          className="rounded-2xl p-5"
          style={{ background: 'var(--vz-bg-card)', border: '1px solid var(--vz-border-subtle)' }}
          data-testid="semaforo-section"
        >
          <SemaforoPanel filters={effectiveFilters} />
        </div>

        {/* ── Días sin incidentes por servicio ──────────────────────────── */}
        <div
          className="rounded-2xl p-5"
          style={{ background: 'var(--vz-bg-card)', border: '1px solid var(--vz-border-subtle)' }}
          data-testid="dias-sin-incidentes-section"
        >
          <DiasSinIncidentesPanel />
        </div>

        {/* ── Evolución combinada (tendencias multi-serie) ─────────────── */}
        <div
          className="rounded-2xl p-5"
          style={{ background: 'var(--vz-bg-card)', border: '1px solid var(--vz-border-subtle)' }}
          data-testid="tendencias-section-wrapper"
        >
          <TendenciasSection filters={effectiveFilters} />
        </div>
      </div>
    </div>
  );
}
